import {
  Button,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import { useState } from "react";
import SearchBar from "../../../components/SearchBar";
import CommonBreadcrumb from "../../../components/CommonBreadcrumb";
import SwitchToggle from "../../../components/SwitchToggle";
import AddModel from "../../../Modals/addModel";
import React from "react";
import { useGetBrandsQuery, useGetCategoriesQuery, useGetModelsQuery, useDeleteModelMutation, useUpdateModelMutation } from "../../../service/features/api";
import Pagination from "../../../components/Panigation";
import { usePermission } from "../../../hooks/usePermission";
import Can from "../../../components/Can";
import moment from "moment";
import DeleteConfirmModal from "../../../Modals/DeleteConfirmModal";
import { toast } from "react-toastify";

const ManageModel = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("Vehicle Specification", "isEdit");
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState('');
  const [brand, setBrand] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedModel, setSelectedModel] = useState<any>(null);

  // Queries
  const { data: categoriesData } = useGetCategoriesQuery({ page: 1, limit: 100, brandId: brand });
  const { data: brandsData } = useGetBrandsQuery({ page: 1, limit: 100 });

  const { data: modelsData, isLoading, isFetching } = useGetModelsQuery({
    page,
    limit,
    search: searchTerm,
    categoryId: category || undefined,
    brandId: brand || undefined
  });

  const [deleteModel, { isLoading: isDeleting }] = useDeleteModelMutation();
  const [updateModel] = useUpdateModelMutation();

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setCategory(event.target.value as string);
    setPage(1); // Reset page on filter change
  };

  const handleBrandChange = (event: SelectChangeEvent) => {
    setBrand(event.target.value as string);
    setCategory('');
    setPage(1);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedModel(null);
  };

  const handleEdit = (model: any) => {
    setSelectedModel(model);
    setOpenModal(true);
  };

  const handleDeleteClick = (model: any) => {
    setSelectedModel(model);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedModel?._id) return;
    try {
      await deleteModel(selectedModel._id).unwrap();
      toast.success("Model deleted successfully");
      setOpenDeleteModal(false);
      setSelectedModel(null);
      if (models.length === 1 && page > 1) {
        setPage(page - 1);
      }
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete model");
    }
  };

  const handleToggle = async (model: any, field: string, value: boolean) => {
    const payload = {
      id: model._id,
      name: model.name,
      categoryId: model.categoryId || (model.category?.map((c: any) => c._id) || []),
      brandId: model.brandId || model.brand?._id,
      image: model.image, // Ensure image is passed if it exists
      isPopular: model.isPopular,
      isActive: model.isActive,
      type: model.type || model.vehicleType,
      [field]: value
    };

    try {
      await updateModel(payload).unwrap();
      toast.success("Updated successfully");
    } catch (err: any) {
      toast.error(err?.data?.message || "Update failed");
    }
  };

  const models = modelsData?.data?.data || [];
  const paginator = modelsData?.data?.paginator || {};

  return (
    <>
      <CommonBreadcrumb
        title="إدارة الموديلات / Model Management"
        breadcrumbItems={[
          { label: "لوحة المعلومات / Dashboard", onClick: () => navigate("/dashboard") },
          { label: "المركبات المدرجة / Listed Vehicles", onClick: () => navigate("/manage-vehicles") },
          { label: "إدارة الموديلات / Model Management" },
        ]}
      />

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={(term) => {
                setSearchTerm(term);
                setPage(1);
              }}
              placeholder="البحث في الموديلات... / Search Models..."
            />
            <div className="form">
              <div className="control_group">
                <Select
                  fullWidth
                  value={category}
                  onChange={handleCategoryChange}
                  displayEmpty
                >
                  <MenuItem value="">حدد الفئة / Select Category</MenuItem>
                  {categoriesData?.data?.data?.map((cat: any) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <div className="control_group">
                <Select
                  fullWidth
                  value={brand}
                  onChange={handleBrandChange}
                  displayEmpty
                >
                  <MenuItem value="">حدد العلامات التجارية / Select Brands</MenuItem>
                  {brandsData?.data?.data?.map((br: any) => (
                    <MenuItem key={br._id} value={br._id}>
                      {br.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>
          </div>
          <div className="right_s">
            <Can module="Vehicle Specification" action="isAdd" isDisabledMode={true}>
              <Button
                className="btn btn_primary"
                onClick={() => {
                  setSelectedModel(null);
                  setOpenModal(true);
                }}
              >
                <AddIcon /> إضافة موديل / Add Model
              </Button>
            </Can>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>م / Sr. No.</TableCell>
                <TableCell>تفاصيل الموديل / Model Detail</TableCell>
                <TableCell>العلامة التجارية / Brand</TableCell>
                <TableCell>الفئات / Categories</TableCell>
                <TableCell>تاريخ الإنشاء / Created Date</TableCell>
                <TableCell>تعطيل/تمكين / Disable/Enable</TableCell>
                <TableCell>شائع / Popular</TableCell>
                <TableCell>إجراءات / Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading || isFetching ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" style={{ padding: '2rem' }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : models.length > 0 ? (
                models.map((row: any, index: number) => (
                  <TableRow hover key={row._id}>
                    <TableCell>{(paginator.currentPage - 1) * paginator.perPage + index + 1}</TableCell>
                    <TableCell>
                      <div className="user_block">
                        <figure className="no_radius">
                          <img
                            src={row.image || "/static/images/placeholder.png"}
                            alt={row.name}
                            onError={(e: any) => { e.target.src = "/static/images/placeholder.png" }}
                          />
                        </figure>
                        <p>
                          <b>{row.name || "--"}</b>
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>{row.brand?.name || "--"}</TableCell>
                    <TableCell>
                      {(row.category || row.categories || row.categoryId)?.map((c: any) => c.name).join(", ") || (Array.isArray(row.categoryName) ? row.categoryName.join(", ") : row.categoryName) || "--"}
                    </TableCell>
                    <TableCell>{moment(row.createdAt).format('DD-MMM-YYYY')}</TableCell>
                    <TableCell>
                      {canEdit ? (
                        <SwitchToggle
                          checked={row.isActive}
                          onChange={(e) => handleToggle(row, "isActive", e.target.checked)}
                        />
                      ) : (
                        <span className={`status_btn ${row.isActive ? "c_dark_info" : "c_dark_warning"}`}>
                          {row.isActive ? "Enabled" : "Disabled"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {canEdit ? (
                        <SwitchToggle
                          checked={row.isPopular}
                          onChange={(e) => handleToggle(row, "isPopular", e.target.checked)}
                        />
                      ) : (
                        <span className={`status_btn ${row.isPopular ? "c_dark_info" : "c_dark_warning"}`}>
                          {row.isPopular ? "Yes" : "No"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <Can module="Vehicle Specification" action="isEdit" isDisabledMode={true}>
                          <Tooltip title="Edit" arrow>
                            <IconButton onClick={() => handleEdit(row)}>
                              <img src="/static/images/edit2_icon.svg" alt="Edit" />
                            </IconButton>
                          </Tooltip>
                        </Can>
                        <Can module="Vehicle Specification" action="isDelete" isDisabledMode={true}>
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              onClick={() => handleDeleteClick(row)}
                            >
                              <img src="/static/images/trash2_icon.svg" alt="Delete" />
                            </IconButton>
                          </Tooltip>
                        </Can>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    لا توجد موديلات / No models found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {paginator && paginator.pageCount > 1 && (
          <Pagination
            module={models}
            page={page}
            onPageChange={(p) => setPage(p)}
            totalPages={paginator.pageCount}
            totalCount={paginator.itemCount}
            setPage={setPage}
          />
        )}
      </div>

      <AddModel
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        modelData={selectedModel}
      />

      <DeleteConfirmModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        message={`Are you sure you want to delete the model "${selectedModel?.name}"?`}
      />
    </>
  );
};


export default ManageModel;
